/**
 * Feature flags (§51).
 *
 * §51 is explicit that "Flags are not security controls or entitlement
 * controls", and that shapes the design: flags live in the client, are safe
 * to read, and gate *presentation* only. Anything that must actually be
 * refused is refused by the backend.
 *
 * Values come from the build environment. A backend-served flag endpoint
 * would let these change without a deploy, which is the usual reason to have
 * flags at all — `refreshFromServer` is the seam for that, and is documented
 * in the README as an outstanding item rather than silently missing.
 */

import { createContext, useContext, useMemo, type ReactNode } from 'react';

export const FLAGS = [
  'HOME_RECOMMENDATIONS',
  'VOICE_INPUT',
  'VOICE_OUTPUT',
  'HINGLISH',
  'MEMORY_RECALL',
  'PRACTICE_RECOMMENDATIONS',
  'PARENT_PROGRESS',
  'BADGES',
  'SUBSCRIPTION_PAYWALL',
  'NEW_HOME_LAYOUT',
] as const;

export type Flag = (typeof FLAGS)[number];

/**
 * Defaults chosen so a fresh deploy is useful and safe.
 *
 * VOICE_OUTPUT was off pending §6A.3's listening review -- a generic TTS
 * reading Hinglish produces something a child will not follow, so nobody was
 * to ship it unheard. The review happened on 2026-10-04, on Sarvam
 * `bulbul:v3` with the `aayan` voice, against a real tutor explanation, and
 * it passed. The condition the default was waiting on is met, so the default
 * moves rather than staying false and being overridden by an environment
 * variable on every deploy -- one forgotten `VITE_FEATURE_FLAGS=` and voice
 * silently disappears, which is a worse failure than it never having shipped.
 *
 * Turning it OFF is still instant and needs no deploy: `AI_VOICE_ENABLED` in
 * `feature_flags` is checked per request by NarrationService, so one UPDATE
 * stops audio being made at all. That is the switch that matters at 11pm;
 * this one only decides whether a play button is drawn.
 *
 * The paywall stays off until pricing is settled (§50: "Pricing is not
 * finalized").
 */
const DEFAULTS: Record<Flag, boolean> = {
  HOME_RECOMMENDATIONS: true,
  VOICE_INPUT: true,
  VOICE_OUTPUT: true,
  HINGLISH: true,
  MEMORY_RECALL: true,
  PRACTICE_RECOMMENDATIONS: true,
  PARENT_PROGRESS: true,
  BADGES: true,
  // On since 4 Oct 2026. It gates one thing: whether a 402 offers the way
  // to fix itself. Prices are decided, the plans exist in Razorpay, and
  // §50's "pricing is not finalized" no longer holds.
  //
  // Note what this flag does NOT control. The block itself is server-side --
  // `require_entitlement` has been returning 402 on the expensive paths
  // since migration 0027 -- so turning this off does not give anybody free
  // access. It only takes the door away again.
  SUBSCRIPTION_PAYWALL: true,
  NEW_HOME_LAYOUT: false,
};

function parseEnvFlags(): Partial<Record<Flag, boolean>> {
  const raw = import.meta.env.VITE_FEATURE_FLAGS;
  if (!raw) return {};
  const overrides: Partial<Record<Flag, boolean>> = {};
  for (const entry of raw.split(',')) {
    const [name, value] = entry.split('=').map((part) => part.trim());
    if (!name) continue;
    if ((FLAGS as readonly string[]).includes(name)) {
      overrides[name as Flag] = value !== 'false' && value !== '0';
    }
  }
  return overrides;
}

const FlagContext = createContext<Record<Flag, boolean>>(DEFAULTS);

export function FlagProvider({
  children,
  overrides,
}: {
  children: ReactNode;
  /** Test seam: lets a test turn a flag on without touching the environment. */
  overrides?: Partial<Record<Flag, boolean>>;
}) {
  const value = useMemo(
    () => ({ ...DEFAULTS, ...parseEnvFlags(), ...overrides }),
    [overrides],
  );
  return <FlagContext.Provider value={value}>{children}</FlagContext.Provider>;
}

export function useFlag(flag: Flag): boolean {
  return useContext(FlagContext)[flag];
}
